import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import schoolRoutes from "./routes/schoolRoutes";
import authRoutes from "./routes/authRoutes";
import { usePiniaStore } from "../stores/store";
import { checkIfArrayElementsMatch } from "../methods/pathRedirection";
import { Permissions } from "@/constants/access";
import {
    BACSInstructionTypeKebabCase,
    BatchRouteNames,
} from "@/constants/bacs";
import { extractCorePermissions } from "@/methods/permissionsHelpers";
import { AuthRouteNames, OrganisationRouteNames } from "@/constants/routes";
import { configSentryError } from "@/methods/sentry";
import { useOrganisationStore } from "@/stores/organisationStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { loadUserOrganisations } from "@/apis/esendaV2/user";
import { fetchAuthSession } from "aws-amplify/auth";
import { ORGANISATION_QUERY_PARAM } from "@/constants/routes";
import { activateOrganisation } from "@/methods/organisationSwitching";

const routes: Array<RouteRecordRaw> = [...authRoutes, ...schoolRoutes];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

/**
 * Loads the user's organisations from the current Cognito session.
 * @returns {Promise<boolean>} True if a signed-in session was found, false otherwise.
 */
const hydrateOrganisationsFromSession = async (): Promise<boolean> => {
    // fetchAuthSession rejects when there is no valid/refreshable session; let
    // the route-level authGuard handle that case rather than aborting the navigation.
    let session;
    try {
        session = await fetchAuthSession();
    } catch {
        return false;
    }

    const email = session.tokens?.idToken?.payload.email;
    if (typeof email !== "string" || email.trim() === "") return false;

    const pStore = usePiniaStore();
    pStore.setUserEmail(email);
    pStore.setIsAuthenticated(true);
    await loadUserOrganisations(email);
    return true;
};

/**
 * Resolves an optional organisation deep-link before protected route components
 * mount. Resource links can include `?organisation=<shortcode>`; the shortcode
 * is only a requested context and must be present in the profile response
 * before it can become active.
 *
 * Any protected route opened in a fresh tab hydrates the session-scoped
 * organisation store here, so components never read an empty shortcode. Switching
 * before component mount ensures the first resource request uses the correct
 * organisation rather than making a request with the previously active one.
 */
router.beforeEach(async (to) => {
    const pStore = usePiniaStore();
    const organisationStore = useOrganisationStore();
    const onboardingStore = useOnboardingStore();

    const requestedOrganisation = to.query[ORGANISATION_QUERY_PARAM];
    const hasRequestedOrganisation = typeof requestedOrganisation === "string";
    const requiresOrganisationContext = to.matched.some(
        ({ meta }) =>
            Array.isArray(meta.requiredPermissions) &&
            meta.requiredPermissions.length > 0,
    );

    // A fresh tab may have a valid Cognito session but no hydrated organisation store.
    if (
        (hasRequestedOrganisation || requiresOrganisationContext) &&
        (organisationStore.getAvailableOrganisations.length === 0 ||
            (!onboardingStore.initialised && pStore.getIsAuthenticated))
    ) {
        const hasSession = await hydrateOrganisationsFromSession();
        if (!hasSession && requiresOrganisationContext) {
            return {
                name: AuthRouteNames.LOGIN,
                query: { origin: to.fullPath },
            };
        }

        if (
            hasSession &&
            organisationStore.getAvailableOrganisations.length === 0
        ) {
            return "/unauthorised-access";
        }
    }

    if (hasRequestedOrganisation) {
        // Never trust a shortcode from a URL without checking the profile response.
        const requestedIsAllowed =
            organisationStore.getAvailableOrganisations.some(
                ({ shortcode }) => shortcode === requestedOrganisation,
            );
        if (!requestedIsAllowed) return "/unauthorised-access";

        if (
            requestedOrganisation !==
            organisationStore.getActiveOrganisationShortcode
        ) {
            // The keyed RouterView remounts organisation pages after this guard completes.
            const activated = activateOrganisation(requestedOrganisation);
            // Never load the requested URL under the previous organisation
            if (!activated) return false;
        }
    }

    // user permissions
    const storedUserPermissions = pStore.getPermissions ?? null;
    // meta route properties
    const isAuthenticated = pStore.getIsAuthenticated;

    if (
        isAuthenticated &&
        !onboardingStore.initialised &&
        pStore.getUserEmail
    ) {
        onboardingStore.initialiseOnboarding(
            pStore.getUserEmail,
            organisationStore.getAvailableOrganisations,
        );
    }

    if (
        isAuthenticated &&
        onboardingStore.required &&
        (requiresOrganisationContext || to.path === "/") &&
        to.name !== OrganisationRouteNames.CUSTOMER_ONBOARDING &&
        to.name !== "Support"
    ) {
        return { name: OrganisationRouteNames.CUSTOMER_ONBOARDING };
    }
    if (
        isAuthenticated &&
        !onboardingStore.required &&
        !onboardingStore.inProgress &&
        to.name === OrganisationRouteNames.CUSTOMER_ONBOARDING
    ) {
        return "/organisation/overview";
    }

    const requiredPermissions: string[] =
        (to.meta.requiredPermissions as string[]) || [];

    const requiresValidBacsInstructionType = to.matched.some(
        (x) => x.meta.checkInstructionType,
    );

    const currentUserPermissions = storedUserPermissions
        ? extractCorePermissions(storedUserPermissions)
        : [];

    const basePermissions = [Permissions.BASE];
    const hasBaseAccess = checkIfArrayElementsMatch(
        basePermissions,
        currentUserPermissions,
    );

    /**
     * Redirects to 404 if the route requiresValidBacsInstructionType and the
     * id instructionType is not a valid instruction type
     */
    if (requiresValidBacsInstructionType) {
        if (
            !Object.values(BACSInstructionTypeKebabCase).includes(
                to.params.instructionType as string,
            )
        ) {
            return "/not-found";
        }
    }

    /** Handle authenticated users navigating to root */
    if (isAuthenticated && to.path === "/") {
        if (hasBaseAccess) {
            /** Redirecting to overview */
            return "/organisation/overview";
        }
    }

    /** Check if the user permissions list includes the route's required permissions */
    if (requiredPermissions.length > 0) {
        const hasRequiredPermissions = requiredPermissions.every((permission) =>
            currentUserPermissions.includes(permission),
        );

        if (!hasRequiredPermissions) {
            return "/unauthorised-access";
        }
    }
});

export const redirectTo404 = () => {
    router.push({ name: "404" });
};

export const redirectToAccessDenied = () => {
    router.push({ name: "UnauthorisedAccess" });
};

export const changeRoute = (path: string) => {
    router.push(path).catch((err) => {
        console.log("Route error: " + err);
    });
};

/**
 * Push to a named route with params and optional query, with robust fallbacks.
 * - Primary: `router.push({ name, params, query })` (adds a history entry)
 * - Fallback 1: Hard navigation via `window.location.assign(router.resolve(location).href)`
 * - Fallback 2: `window.location.reload()` if resolution fails
 * @param {string | OrganisationRouteNames | AuthRouteNames | BatchRouteNames} name - The route name defined in the router
 * @param {Record<string, any>} params - The route params for the target
 * @param {Record<string, any>} query - Optional query parameters to append
 */
export const pushRouteWithFallback = (
    name: string | OrganisationRouteNames | AuthRouteNames | BatchRouteNames,
    params?: Record<string, any>,
    query?: Record<string, any>,
): void => {
    const location = { name, params, query };

    router.push(location).catch((_error) => {
        try {
            const target = router.resolve(location).href;
            // Add a new history entry
            window.location.assign(target);
        } catch (resolveError) {
            configSentryError(
                "router/index.ts",
                "pushRouteWithFallback:router.resolve",
                `Failed to resolve route "${String(name)}" with params ${JSON.stringify(
                    params,
                )} and query ${JSON.stringify(query)}. Error: ${resolveError}`,
            );
            window.location.reload();
        }
    });
};

/**
 * Redirect to a route by name or path while preserving the existing history state
 * If the redirection fails, reload the page to recover the application state.
 * @param {any} route
 * @return {void}
 */
export const redirectToRoute = (route: any): void => {
    const existingState = { ...history.state };

    router
        .replace({
            ...route,
            state: existingState,
        })
        .catch((_error) => {
            try {
                const target = router.resolve(route).href;
                window.location.replace(target);
            } catch (resolveError) {
                configSentryError(
                    "router/index.ts",
                    "redirectToRoute:router.resolve",
                    `Failed to resolve route ${JSON.stringify(route)}. Error: ${resolveError}`,
                );
                window.location.reload();
            }
        });
};

export const navigateBack = (step: number) => {
    /**
     * Navigate back
     * @param {number} step - indicates by how many steps to go backward in the history stack
     */
    router.go(step);
};

/**
 * Check if the current route is the login page
 * @returns { boolean }
 */
export const routeIsLogin = (): boolean => {
    const route = router.currentRoute.value;
    return (
        route.name === AuthRouteNames.LOGIN ||
        route.name === AuthRouteNames.SSO_LOGIN
    );
};

export default router;
